import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { deleteField, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import Navbar from "../Components/Navbar/navbar";
import { limparCacheLogoRelatorio } from "../Components/LogoRelatorio/LogoRelatorio";
import { auth, db, storage } from "../Config/firebase";
import { AMBIENTE_PRODUCAO, LOGO_MARCA_DAGUA, LOGO_RELATORIO_PADRAO, NOME_PLATAFORMA } from "../Config/branding";
import { getUsuarioAtualComTenant } from "../Utils/tenant";
import { confirmarSistema } from "../Components/PopupSistema/PopupSistema";

const LOGO_PADRAO = LOGO_RELATORIO_PADRAO;
const TIPOS_PERMITIDOS = ["image/png", "image/jpeg", "image/webp"];
const LIMITE_BYTES = 5 * 1024 * 1024;

export default function ConfigLogoRelatorios() {
  const [usuarioAtual, setUsuarioAtual] = useState(null);
  const [logo, setLogo] = useState({ logoUrl: "", storagePath: "" });
  const [logoCalibracaoPrensa, setLogoCalibracaoPrensa] = useState({ logoUrl: "", storagePath: "" });
  const [marcaDagua, setMarcaDagua] = useState({ logoUrl: "", storagePath: "" });
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState(null);

  useEffect(() => {
    let cancelado = false;

    async function carregar() {
      try {
        const usuario = await getUsuarioAtualComTenant();
        if (cancelado) return;
        setUsuarioAtual(usuario);

        if (!usuario.tenantId) throw new Error("USUARIO_SEM_TENANT_ID");
        const snap = await getDoc(doc(db, "tenant_branding", usuario.tenantId));
        if (!cancelado && snap.exists()) {
          setLogo({
            logoUrl: snap.data()?.logoUrl || "",
            storagePath: snap.data()?.storagePath || "",
          });
          setLogoCalibracaoPrensa({
            logoUrl: snap.data()?.logoCalibracaoPrensaUrl || "",
            storagePath: snap.data()?.logoCalibracaoPrensaStoragePath || "",
          });
          setMarcaDagua({
            logoUrl: snap.data()?.marcaDaguaUrl || "",
            storagePath: snap.data()?.marcaDaguaStoragePath || "",
          });
        }
      } catch (error) {
        console.error("Erro ao carregar identidade visual:", error);
        if (!cancelado) setMensagem({ tipo: "error", texto: "Não foi possível carregar a logo da empresa." });
      } finally {
        if (!cancelado) setCarregando(false);
      }
    }

    carregar();
    return () => {
      cancelado = true;
    };
  }, []);

  async function selecionarArquivo(event) {
    const arquivo = event.target.files?.[0];
    event.target.value = "";
    if (!arquivo || !usuarioAtual?.tenantId) return;

    if (!TIPOS_PERMITIDOS.includes(arquivo.type)) {
      setMensagem({ tipo: "error", texto: "Selecione uma imagem PNG, JPG ou WEBP." });
      return;
    }
    if (arquivo.size > LIMITE_BYTES) {
      setMensagem({ tipo: "error", texto: "A imagem deve ter no máximo 5 MB." });
      return;
    }

    try {
      setSalvando(true);
      setMensagem(null);
      const extensao = arquivo.type === "image/png" ? "png" : arquivo.type === "image/webp" ? "webp" : "jpg";
      const storagePath = `tenants/${usuarioAtual.tenantId}/branding/logo-relatorios.${extensao}`;
      const logoRef = ref(storage, storagePath);

      await uploadBytes(logoRef, arquivo, {
        contentType: arquivo.type,
        customMetadata: { tenantId: usuarioAtual.tenantId },
      });
      const logoUrl = await getDownloadURL(logoRef);

      await setDoc(
        doc(db, "tenant_branding", usuarioAtual.tenantId),
        {
          tenantId: usuarioAtual.tenantId,
          tenantNome: usuarioAtual.tenantNome || "",
          logoUrl,
          storagePath,
          atualizadoEm: serverTimestamp(),
          atualizadoPor: auth.currentUser?.uid || null,
        },
        { merge: true }
      );

      if (logo.storagePath && logo.storagePath !== storagePath) {
        deleteObject(ref(storage, logo.storagePath)).catch(() => {});
      }

      setLogo({ logoUrl, storagePath });
      localStorage.setItem("logoRelatorioUrl", logoUrl);
      localStorage.setItem(`logoSistemaUrl:${usuarioAtual.tenantId}`, logoUrl);
      limparCacheLogoRelatorio(usuarioAtual.tenantId);
      window.dispatchEvent(new CustomEvent("tenant-logo-atualizada", {
        detail: { tenantId: usuarioAtual.tenantId, logoUrl },
      }));
      setMensagem({ tipo: "success", texto: "Logo do sistema e dos relatórios atualizada com sucesso." });
    } catch (error) {
      console.error("Erro ao salvar logo:", error);
      setMensagem({ tipo: "error", texto: "Não foi possível salvar a logo. Verifique suas permissões." });
    } finally {
      setSalvando(false);
    }
  }

  async function removerLogo() {
    if (!usuarioAtual?.tenantId || !(await confirmarSistema("Deseja remover a logo personalizada do sistema e dos relatórios?"))) return;

    try {
      setSalvando(true);
      if (logo.storagePath) {
        await deleteObject(ref(storage, logo.storagePath)).catch((error) => {
          if (error?.code !== "storage/object-not-found") throw error;
        });
      }
      await setDoc(
        doc(db, "tenant_branding", usuarioAtual.tenantId),
        {
          logoUrl: deleteField(),
          storagePath: deleteField(),
          atualizadoEm: serverTimestamp(),
          atualizadoPor: auth.currentUser?.uid || null,
        },
        { merge: true }
      );
      setLogo({ logoUrl: "", storagePath: "" });
      localStorage.removeItem("logoRelatorioUrl");
      localStorage.removeItem(`logoSistemaUrl:${usuarioAtual.tenantId}`);
      limparCacheLogoRelatorio(usuarioAtual.tenantId);
      window.dispatchEvent(new CustomEvent("tenant-logo-atualizada", {
        detail: { tenantId: usuarioAtual.tenantId, logoUrl: "" },
      }));
      setMensagem({ tipo: "success", texto: "Logo removida. O sistema e os relatórios usarão a logo padrão." });
    } catch (error) {
      console.error("Erro ao remover logo:", error);
      setMensagem({ tipo: "error", texto: "Não foi possível remover a logo." });
    } finally {
      setSalvando(false);
    }
  }

  async function selecionarLogoCalibracaoPrensa(event) {
    const arquivo = event.target.files?.[0];
    event.target.value = "";
    if (!arquivo || !usuarioAtual?.tenantId) return;

    if (!TIPOS_PERMITIDOS.includes(arquivo.type)) {
      setMensagem({ tipo: "error", texto: "Selecione uma imagem PNG, JPG ou WEBP." });
      return;
    }
    if (arquivo.size > LIMITE_BYTES) {
      setMensagem({ tipo: "error", texto: "A imagem deve ter no máximo 5 MB." });
      return;
    }

    try {
      setSalvando(true);
      setMensagem(null);
      const extensao = arquivo.type === "image/png" ? "png" : arquivo.type === "image/webp" ? "webp" : "jpg";
      const storagePath = `tenants/${usuarioAtual.tenantId}/branding/logo-calibracao-prensa.${extensao}`;
      const logoRef = ref(storage, storagePath);

      await uploadBytes(logoRef, arquivo, {
        contentType: arquivo.type,
        customMetadata: { tenantId: usuarioAtual.tenantId },
      });
      const logoUrl = await getDownloadURL(logoRef);

      await setDoc(
        doc(db, "tenant_branding", usuarioAtual.tenantId),
        {
          tenantId: usuarioAtual.tenantId,
          tenantNome: usuarioAtual.tenantNome || "",
          logoCalibracaoPrensaUrl: logoUrl,
          logoCalibracaoPrensaStoragePath: storagePath,
          atualizadoEm: serverTimestamp(),
          atualizadoPor: auth.currentUser?.uid || null,
        },
        { merge: true }
      );

      if (
        logoCalibracaoPrensa.storagePath &&
        logoCalibracaoPrensa.storagePath !== storagePath
      ) {
        deleteObject(ref(storage, logoCalibracaoPrensa.storagePath)).catch(() => {});
      }

      setLogoCalibracaoPrensa({ logoUrl, storagePath });
      limparCacheLogoRelatorio(usuarioAtual.tenantId);
      window.dispatchEvent(new CustomEvent("tenant-logo-calibracao-prensa-atualizada", {
        detail: { tenantId: usuarioAtual.tenantId, logoUrl },
      }));
      setMensagem({ tipo: "success", texto: "Logo dos certificados de calibração de prensa atualizada com sucesso." });
    } catch (error) {
      console.error("Erro ao salvar logo de calibração de prensa:", error);
      setMensagem({ tipo: "error", texto: "Não foi possível salvar a logo. Verifique suas permissões." });
    } finally {
      setSalvando(false);
    }
  }

  async function removerLogoCalibracaoPrensa() {
    if (
      !usuarioAtual?.tenantId ||
      !(await confirmarSistema("Deseja remover a logo específica dos certificados de calibração de prensa?"))
    ) return;

    try {
      setSalvando(true);
      if (logoCalibracaoPrensa.storagePath) {
        await deleteObject(ref(storage, logoCalibracaoPrensa.storagePath)).catch((error) => {
          if (error?.code !== "storage/object-not-found") throw error;
        });
      }
      await setDoc(
        doc(db, "tenant_branding", usuarioAtual.tenantId),
        {
          logoCalibracaoPrensaUrl: deleteField(),
          logoCalibracaoPrensaStoragePath: deleteField(),
          atualizadoEm: serverTimestamp(),
          atualizadoPor: auth.currentUser?.uid || null,
        },
        { merge: true }
      );
      setLogoCalibracaoPrensa({ logoUrl: "", storagePath: "" });
      limparCacheLogoRelatorio(usuarioAtual.tenantId);
      window.dispatchEvent(new CustomEvent("tenant-logo-calibracao-prensa-atualizada", {
        detail: { tenantId: usuarioAtual.tenantId, logoUrl: logo.logoUrl || "" },
      }));
      setMensagem({ tipo: "success", texto: "Logo específica removida. Os certificados usarão a logo geral." });
    } catch (error) {
      console.error("Erro ao remover logo de calibração de prensa:", error);
      setMensagem({ tipo: "error", texto: "Não foi possível remover a logo específica." });
    } finally {
      setSalvando(false);
    }
  }

  async function selecionarMarcaDagua(event) {
    const arquivo = event.target.files?.[0];
    event.target.value = "";
    if (!arquivo || !usuarioAtual?.tenantId) return;

    if (!TIPOS_PERMITIDOS.includes(arquivo.type)) {
      setMensagem({ tipo: "error", texto: "Selecione uma imagem PNG, JPG ou WEBP." });
      return;
    }
    if (arquivo.size > LIMITE_BYTES) {
      setMensagem({ tipo: "error", texto: "A imagem deve ter no máximo 5 MB." });
      return;
    }

    try {
      setSalvando(true);
      setMensagem(null);
      const extensao = arquivo.type === "image/png" ? "png" : arquivo.type === "image/webp" ? "webp" : "jpg";
      const storagePath = `tenants/${usuarioAtual.tenantId}/branding/marca-dagua-relatorios.${extensao}`;
      const imagemRef = ref(storage, storagePath);

      await uploadBytes(imagemRef, arquivo, {
        contentType: arquivo.type,
        customMetadata: { tenantId: usuarioAtual.tenantId },
      });
      const marcaDaguaUrl = await getDownloadURL(imagemRef);

      await setDoc(
        doc(db, "tenant_branding", usuarioAtual.tenantId),
        {
          tenantId: usuarioAtual.tenantId,
          tenantNome: usuarioAtual.tenantNome || "",
          marcaDaguaUrl,
          marcaDaguaStoragePath: storagePath,
          atualizadoEm: serverTimestamp(),
          atualizadoPor: auth.currentUser?.uid || null,
        },
        { merge: true }
      );

      if (marcaDagua.storagePath && marcaDagua.storagePath !== storagePath) {
        deleteObject(ref(storage, marcaDagua.storagePath)).catch(() => {});
      }

      setMarcaDagua({ logoUrl: marcaDaguaUrl, storagePath });
      limparCacheLogoRelatorio(usuarioAtual.tenantId);
      window.dispatchEvent(new CustomEvent("tenant-marca-dagua-atualizada", {
        detail: { tenantId: usuarioAtual.tenantId, marcaDaguaUrl },
      }));
      setMensagem({ tipo: "success", texto: "Marca d’água dos relatórios atualizada com sucesso." });
    } catch (error) {
      console.error("Erro ao salvar marca d’água:", error);
      setMensagem({ tipo: "error", texto: "Não foi possível salvar a marca d’água. Verifique suas permissões." });
    } finally {
      setSalvando(false);
    }
  }

  async function removerMarcaDagua() {
    if (
      !usuarioAtual?.tenantId ||
      !(await confirmarSistema("Deseja remover a marca d’água personalizada dos relatórios?"))
    ) return;

    try {
      setSalvando(true);
      if (marcaDagua.storagePath) {
        await deleteObject(ref(storage, marcaDagua.storagePath)).catch((error) => {
          if (error?.code !== "storage/object-not-found") throw error;
        });
      }
      await setDoc(
        doc(db, "tenant_branding", usuarioAtual.tenantId),
        {
          marcaDaguaUrl: deleteField(),
          marcaDaguaStoragePath: deleteField(),
          atualizadoEm: serverTimestamp(),
          atualizadoPor: auth.currentUser?.uid || null,
        },
        { merge: true }
      );
      setMarcaDagua({ logoUrl: "", storagePath: "" });
      limparCacheLogoRelatorio(usuarioAtual.tenantId);
      window.dispatchEvent(new CustomEvent("tenant-marca-dagua-atualizada", {
        detail: { tenantId: usuarioAtual.tenantId, marcaDaguaUrl: "" },
      }));
      setMensagem({ tipo: "success", texto: "Marca d’água removida. Os relatórios usarão a imagem padrão." });
    } catch (error) {
      console.error("Erro ao remover marca d’água:", error);
      setMensagem({ tipo: "error", texto: "Não foi possível remover a marca d’água." });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <Navbar />
      <Box sx={{ maxWidth: 900, mx: "auto", px: 2, py: 5 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Logo do sistema e dos relatórios
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {AMBIENTE_PRODUCAO
            ? `Esta logo será utilizada na navegação do sistema e automaticamente nos relatórios da empresa ${usuarioAtual?.tenantNome || NOME_PLATAFORMA}.`
            : "Esta logo será utilizada na navegação do sistema e automaticamente nos relatórios da sua empresa."}
        </Typography>

        {mensagem && <Alert severity={mensagem.tipo} sx={{ mb: 2 }}>{mensagem.texto}</Alert>}

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
          {carregando ? (
            <CircularProgress />
          ) : (
            <Stack spacing={3} alignItems="flex-start">
              <Box
                sx={{
                  width: "100%",
                  minHeight: 220,
                  display: "grid",
                  placeItems: "center",
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  p: 3,
                }}
              >
                <Box
                  component="img"
                  src={logo.logoUrl || LOGO_PADRAO}
                  alt="Pré-visualização da logo do sistema e dos relatórios"
                  sx={{ maxWidth: "100%", maxHeight: 170, objectFit: "contain" }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary">
                Recomendação: PNG com fundo transparente, formato horizontal e boa resolução. Máximo de 5 MB.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button component="label" variant="contained" startIcon={<CloudUploadIcon />} disabled={salvando}>
                  {logo.logoUrl ? "Substituir logo" : "Enviar logo"}
                  <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={selecionarArquivo} />
                </Button>
                {logo.logoUrl && (
                  <Button color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={removerLogo} disabled={salvando}>
                    Remover logo
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
        </Paper>

        <Typography variant="h5" fontWeight={700} sx={{ mt: 4, mb: 1 }}>
          Logo dos certificados de calibração de prensa
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Utilizada somente nos relatórios de calibração de prensa de concreto e solos. Sem uma logo específica, será usada a logo geral.
        </Typography>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
          {carregando ? (
            <CircularProgress />
          ) : (
            <Stack spacing={3} alignItems="flex-start">
              <Box
                sx={{
                  width: "100%",
                  minHeight: 220,
                  display: "grid",
                  placeItems: "center",
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  p: 3,
                }}
              >
                <Box
                  component="img"
                  src={logoCalibracaoPrensa.logoUrl || logo.logoUrl || LOGO_PADRAO}
                  alt="Pré-visualização da logo dos certificados de calibração de prensa"
                  sx={{ maxWidth: "100%", maxHeight: 170, objectFit: "contain" }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary">
                Recomendação: PNG com fundo transparente, formato horizontal e boa resolução. Máximo de 5 MB.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button component="label" variant="contained" startIcon={<CloudUploadIcon />} disabled={salvando}>
                  {logoCalibracaoPrensa.logoUrl ? "Substituir logo específica" : "Enviar logo específica"}
                  <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={selecionarLogoCalibracaoPrensa} />
                </Button>
                {logoCalibracaoPrensa.logoUrl && (
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={removerLogoCalibracaoPrensa}
                    disabled={salvando}
                  >
                    Remover logo específica
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
        </Paper>

        <Typography variant="h5" fontWeight={700} sx={{ mt: 4, mb: 1 }}>
          Marca d’água dos relatórios
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Utilizada nos certificados de calibração de prensa e nos termos de locação de equipamentos. Sem uma imagem personalizada, será usada a marca d’água padrão.
        </Typography>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
          {carregando ? (
            <CircularProgress />
          ) : (
            <Stack spacing={3} alignItems="flex-start">
              <Box
                sx={{
                  width: "100%",
                  minHeight: 220,
                  display: "grid",
                  placeItems: "center",
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  p: 3,
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={marcaDagua.logoUrl || LOGO_MARCA_DAGUA}
                  alt="Pré-visualização da marca d’água dos relatórios"
                  sx={{ maxWidth: "75%", maxHeight: 170, objectFit: "contain", opacity: 0.14 }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary">
                Recomendação: PNG com fundo transparente, boa resolução e contraste suave. A opacidade final é aplicada automaticamente pelo relatório. Máximo de 5 MB.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button component="label" variant="contained" startIcon={<CloudUploadIcon />} disabled={salvando}>
                  {marcaDagua.logoUrl ? "Substituir marca d’água" : "Enviar marca d’água"}
                  <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={selecionarMarcaDagua} />
                </Button>
                {marcaDagua.logoUrl && (
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={removerMarcaDagua}
                    disabled={salvando}
                  >
                    Remover marca d’água
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
        </Paper>
      </Box>
    </>
  );
}
